import { useState, useEffect } from 'react'
import {
  Save,
  RefreshCw,
  Server,
  Shield,
  Settings as SettingsIcon,
  Mail,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Upload,
  HelpCircle,
  Globe
} from 'lucide-react'
import { apiService } from '../config/api'
import toast from 'react-hot-toast'
import SettingsGuide from '../components/SettingsGuide'

export default function Settings() {
  const [settings, setSettings] = useState({
    smtp: {
      host: '',
      port: 587,
      username: '',
      password: '',
      secure: false,
      timeout: 10000,
      concurrency: 5,
      rate_limit: 100,
      helo_domain: 'localhost'
    },
    imap: {
      host: '',
      port: 993,
      username: '',
      password: '',
      tls: true
    },
    license: {
      key: '',
      domain: '',
      ip: ''
    },
    system: {
      log_level: 'info',
      cors_origin: 'http://localhost:3000'
    },
    disposable: {
      list_url: 'https://raw.githubusercontent.com/disposable/disposable-email-domains/master/domains.json',
      role_based_emails: ['info', 'support', 'admin', 'contact', 'help', 'service', 'sales', 'marketing', 'newsletter', 'no-reply', 'noreply']
    },
    emailScanner: {
      scanFrom: '',
      smtpCommand: 'EHLO',
      smtpPort: 25,
      timeoutSec: 15,
      batchSize: 10
    },
    uploads: {
      max_upload_size_mb: 10,
      max_email_list_size: 100000,
      import_concurrency: 4,
      max_concurrent_uploads: 2,
      worker_concurrency: 2,
      smtp_concurrency: 5
    },
    rateLimiting: {
      normalCooldown: 1000,
      yahooAolCooldown: 7200000,

      microsoftCooldown: 300000,
      retryAttempts: 3,
      progressUpdateFreq: 10,
      retryDelay1: 3000,
      retryDelay2: 6000,
      retryDelay3: 9000,
      btDailyLimit: 1500,
      talktalkDailyLimit: 1000,
      batchLimitSize: 5,
      batchDelaySec: 15
    },
    queueRetry: {
      maxRetryAttempts: 5,
      tier1DelayMinutes: 2,
      tier2DelayHours: 1,
      tier3DelayHours: 24,
      tier4DelayHours: 48
    },
    providerConcurrency: {
      bigpond: 2,
      bt: 2,
      talktalk: 2,
      yahoo: 5,
      aol: 5,
      gmail: 5,
      outlook: 5,
      mailru: 5,
      default: 5
    },
    deep_verification: false
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [testMxHost, setTestMxHost] = useState('')
  const [isTesting, setIsTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  // New state for error strings, dummy emails, task hash, etc.
  const [errorStrings, setErrorStrings] = useState([]);
  const [dummyEmails, setDummyEmails] = useState([]);
  const [newErrorString, setNewErrorString] = useState('');
  const [newDummyEmail, setNewDummyEmail] = useState({ subject: '', message: '' });
  const [editingDummyEmail, setEditingDummyEmail] = useState(null);
  const [taskHash, setTaskHash] = useState('');
  const [imapConfig, setImapConfig] = useState({ host: '', port: 993, username: '', password: '' });
  const [imapTestResult, setImapTestResult] = useState(null);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  // Remove showSmtpModal state and modal logic

  // Bulk scan settings state
  const [bulkScanSettings, setBulkScanSettings] = useState({ batchSize: 10, concurrency: 5, cronSchedule: '0 * * * *' });
  const [isBulkScanLoading, setIsBulkScanLoading] = useState(true);
  const [isBulkScanSaving, setIsBulkScanSaving] = useState(false);

  // Settings guide state
  const [showSettingsGuide, setShowSettingsGuide] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Load error strings and dummy emails on mount
  useEffect(() => {
    loadSettings();
    loadErrorStrings();
    loadDummyEmails();
    loadBulkScanSettings();
    // Generate a task hash (simulate)
    setTaskHash(Math.random().toString(36).substr(2, 32));
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true)
      const response = await apiService.getSettings()

      // Ensure all settings have default values to prevent undefined inputs
      const backendSettings = response.data.data || {};

      // Also fetch license configured status
      let licenseConfigured = false;
      try {
        const lic = await apiService.getLicenseStatus?.();
        if (lic && lic.data && lic.data.data) {
          licenseConfigured = !!lic.data.data.configured;
        }
      } catch (_) { }




      setSettings(prevSettings => ({
        ...prevSettings,
        smtp: {
          ...prevSettings.smtp,
          ...backendSettings.smtp
        },
        imap: {
          ...prevSettings.imap,
          ...backendSettings.imap
        },
        license: {
          ...prevSettings.license,
          ...backendSettings.license,
          configured: licenseConfigured
        },
        system: {
          ...prevSettings.system,
          ...backendSettings.system
        },
        disposable: {
          ...prevSettings.disposable,
          ...backendSettings.disposable
        },
        emailScanner: {
          ...prevSettings.emailScanner,
          ...backendSettings.emailScanner
        },
        uploads: {
          ...prevSettings.uploads,
          ...backendSettings.uploads,
          // Defensive: always ensure max_email_list_size is present
          max_email_list_size: (backendSettings.uploads && backendSettings.uploads.max_email_list_size != null)
            ? backendSettings.uploads.max_email_list_size
            : (prevSettings.uploads.max_email_list_size || 100000),
          worker_concurrency: (backendSettings.uploads && backendSettings.uploads.worker_concurrency != null)
            ? backendSettings.uploads.worker_concurrency
            : (prevSettings.uploads.worker_concurrency || 2),
          smtp_concurrency: (backendSettings.uploads && backendSettings.uploads.smtp_concurrency != null)
            ? backendSettings.uploads.smtp_concurrency
            : (prevSettings.uploads.smtp_concurrency || 5)
        },
        rateLimiting: {
          ...prevSettings.rateLimiting,
          ...backendSettings.rateLimiting
        },
        queueRetry: {
          ...prevSettings.queueRetry,
          ...backendSettings.queueRetry
        },
        providerConcurrency: {
          ...prevSettings.providerConcurrency,
          ...backendSettings.providerConcurrency
        },
        deep_verification: backendSettings.deep_verification !== undefined ? backendSettings.deep_verification : false
      }))

      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
    }
  }

  const loadErrorStrings = async () => {
    try {
      const res = await apiService.getErrorStrings();
      setErrorStrings(res.data.data);
    } catch (e) {
      toast.error('Failed to load error strings');
    }
  };
  const loadDummyEmails = async () => {
    try {
      // Loading dummy emails logging removed for production
      const res = await apiService.getDummyEmails();
      // Dummy emails response logging removed for production

      // Handle both array and single object responses
      let emails = [];
      if (res.data.data) {
        if (Array.isArray(res.data.data)) {
          emails = res.data.data;
        } else if (typeof res.data.data === 'object') {
          // If it's a single object, wrap it in an array
          emails = [res.data.data];
        }
      }

      // Processed dummy emails logging removed for production
      setDummyEmails(emails);
    } catch (e) {
      // no-op
    }
  };

  const loadBulkScanSettings = async () => {
    setIsBulkScanLoading(true);
    try {
      const res = await apiService.getBulkScanSettings();
      setBulkScanSettings({
        batchSize: res.data.data.bulk_batch_size || 10,
        concurrency: res.data.data.bulk_concurrency || 5,
        cronSchedule: res.data.data.bulk_cron_schedule || '0 * * * *'
      });
    } catch (e) {
      toast.error('Failed to load bulk scan settings');
    } finally {
      setIsBulkScanLoading(false);
    }
  };

  const saveBulkScanSettings = async () => {
    setIsBulkScanSaving(true);
    try {
      await apiService.updateBulkScanSettings({
        bulk_batch_size: bulkScanSettings.batchSize,
        bulk_concurrency: bulkScanSettings.concurrency,
        bulk_cron_schedule: bulkScanSettings.cronSchedule
      });
      toast.success('Bulk scan settings saved');
      loadBulkScanSettings();
      loadTaskRunnerStatus();
    } catch (e) {
      toast.error('Failed to save bulk scan settings');
    } finally {
      setIsBulkScanSaving(false);
    }
  };



  const handleSave = async () => {
    const smtpConfigured = settings.smtp?.host && settings.smtp?.username && settings.smtp?.password
    const imapConfigured = settings.imap?.host && settings.imap?.username && settings.imap?.password

    if (smtpConfigured && !settings.deep_verification) {
      toast.error('SMTP details are added but IMAP verification is disabled. Enable IMAP verification or clear SMTP details before saving.')
      return
    }

    if (smtpConfigured && settings.deep_verification && !imapConfigured) {
      toast.error('IMAP verification is enabled but IMAP details are missing. Add IMAP host, username, and password before saving.')
      return
    }

    try {
      setIsSaving(true)
      await apiService.updateSettings(settings)
      toast.success('Settings saved successfully')
      await loadSettings() // Reload settings from backend after save
    } catch (error) {
      // no-op
      toast.error('Failed to save settings')
    } finally {
      setIsSaving(false)
    }
  }

  // Add, delete error string
  const handleAddErrorString = async () => {
    if (!newErrorString) return;
    await apiService.addErrorString(newErrorString);
    setNewErrorString('');
    loadErrorStrings();
  };
  const handleDeleteErrorString = async (id) => {
    await apiService.deleteErrorString(id);
    loadErrorStrings();
  };

  // Add, edit, delete dummy email
  const handleAddDummyEmail = async () => {
    if (!newDummyEmail.subject || !newDummyEmail.message) return;
    await apiService.addDummyEmail(newDummyEmail.subject, newDummyEmail.message);
    setNewDummyEmail({ subject: '', message: '' });
    loadDummyEmails();
  };

  const handleUpdateDummyEmail = async (id, subject, message) => {
    await apiService.updateDummyEmail(id, subject, message);
    loadDummyEmails();
  };

  const handleDeleteDummyEmail = async (id) => {
    await apiService.deleteDummyEmail(id);
    loadDummyEmails();
  };

  const handleTestSmtp = async () => {
    try {
      setIsTesting(true)
      setTestResult(null)
      const smtpConfig = {
        host: settings.smtp.host,
        port: settings.smtp.port,
        username: settings.smtp.username,
        password: settings.smtp.password,
        secure: settings.smtp.secure
      };
      const response = await apiService.testSmtp(smtpConfig);
      setTestResult(response.data);
      toast.success('SMTP test completed')
    } catch (error) {
      // no-op
      toast.error('SMTP test failed')
    } finally {
      setIsTesting(false)
    }
  }

  // IMAP/SMTP connection test
  const handleTestImap = async () => {
    setImapTestResult(null);
    try {
      const imapConfig = {
        host: settings.imap.host,
        port: settings.imap.port,
        username: settings.imap.username,
        password: settings.imap.password,
        tls: settings.imap.tls
      };
      const res = await apiService.testImap(imapConfig);
      setImapTestResult(res.data);
    } catch (e) {
      setImapTestResult({ success: false, message: 'IMAP test failed' });
    }
  };
  // const handleTestSmtp = async () => {
  //   setSmtpTestResult(null);
  //   try {
  //     const res = await apiService.testConnection('smtp', settings.smtp);
  //     setSmtpTestResult(res.data);
  //   } catch (e) {
  //     setSmtpTestResult({ success: false, message: 'SMTP test failed' });
  //   }
  // };

  const updateSetting = (section, key, value) => {
    setSettings(prev => {
      // Ensure the section exists
      const currentSection = prev[section] || {};

      return {
        ...prev,
        [section]: {
          ...currentSection,
          [key]: value
        }
      };
    });
  };

  const hasSmtpCredentials = settings.smtp.host && settings.smtp.username && settings.smtp.password

  // Remove all deep_verification state, UI, and logic
  // Always show SMTP and IMAP fields (not conditional)
  // Remove any references to showSmtpUi, handleDeepVerificationChange, and deep_verification

  // When deep_verification is toggled off, clear SMTP fields
  const handleCloseSmtpModal = () => {
    // This function is no longer needed as modal is removed
  };

  const [inboxCheckLoading, setInboxCheckLoading] = useState(false);
  const [inboxCheckResult, setInboxCheckResult] = useState(null);

  const handleInboxChecker = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address for inbox check');
      return;
    }
    setInboxCheckLoading(true);
    setInboxCheckResult(null);
    try {
      const res = await apiService.inboxChecker(testEmail);
      setInboxCheckResult(res.data);
      if (res.data.delivered) {
        toast.success('Inbox Checker: Delivered!');
      } else {
        toast.error('Inbox Checker: Not delivered');
      }
    } catch (e) {
      toast.error('Inbox Checker failed: ' + (e.response?.data?.error || e.message));
      setInboxCheckResult({ success: false, error: e.response?.data?.error || e.message });
    } finally {
      setInboxCheckLoading(false);
    }
  };

  // Remove workerSettings, isWorkerSettingsLoading, isWorkerSettingsSaving, loadWorkerSettings, saveWorkerSettings
  // Instead, add worker_concurrency and smtp_concurrency to the main settings.uploads or a new settings.worker section
  // Update all input fields for worker concurrency and smtp concurrency to update settings.worker_concurrency and settings.smtp_concurrency directly
  // Remove the <button> for saveWorkerSettings
  // Add a floating/sticky Save Settings button at the bottom right of the viewport

  const [error, setError] = useState(null);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-1/4 mb-6"></div>
          <div className="space-y-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 dark:bg-gray-800 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-10 max-w-5xl mx-auto px-2 sm:px-6 md:px-8">
      {/* Enhanced Page Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl shadow p-6 mb-8 border border-blue-100">
        <div className="flex items-center space-x-4">
          <SettingsIcon className="h-10 w-10 text-blue-500" />
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Settings</h1>
            <p className="mt-1 text-base text-gray-600">Configure SMTP, license, system preferences, and advanced options</p>
          </div>
        </div>
        <button
          onClick={() => setShowSettingsGuide(true)}
          className="btn btn-secondary flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-blue-100 transition-colors"
        >
          <HelpCircle className="h-5 w-5" />
          <span>Settings Guide</span>
        </button>
      </div>

      {/* Save/Refresh Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end mb-4 space-y-2 sm:space-y-0 sm:space-x-3">
        <button
          onClick={loadSettings}
          className="btn btn-secondary shadow-sm hover:shadow-md transition"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn btn-primary shadow-lg hover:shadow-xl transition px-8 py-3 text-lg rounded-full"
            style={{ minWidth: 180 }}
          >
            {isSaving ? (
              <>
                <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>

      {/* 1. License Settings */}
      <section className="card bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 mb-8">
        <div className="card-header flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center space-x-2">
            <Shield className="h-6 w-6 text-blue-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">License Settings</h2>
          </div>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">License Key</label>
            <input
              type="text"
              className="input bg-gray-100 text-gray-600 cursor-not-allowed"
              value={settings.license?.key || ''}
              readOnly
              placeholder="License key (read-only)"
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Your email verifier license key for marketplace activation. (Cannot be modified)</div>
          </div>

          <div className="col-span-2">
            <div className="flex items-center space-x-4">
              <Shield className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">License Status</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {settings.license?.configured ? 'Configured' : 'Not configured'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Email Scanner Settings */}
      <section className="card bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 mb-8">
        <div className="card-header flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-6 w-6 text-blue-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Email Scanner Settings</h2>
          </div>
        </div>

        {/* Email Scanner Information Notification */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-4 mx-6 mt-6 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Email Scanner for SMTP Verification
              </h3>
              <div className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                <p className="mb-2">
                  The <strong>Email Scanner</strong> performs direct SMTP connections to mail servers to verify email addresses exist.
                </p>
                <p className="mb-2">
                  <strong>Scan From Email</strong> and <strong>HELO Domain</strong> are used to establish legitimate SMTP connections that won't be blocked by anti-spam systems.
                </p>
                <p>
                  This method works alongside IMAP verification to provide comprehensive email validation with maximum accuracy.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-6">
          <div>
            <label htmlFor="scan_from" className="block text-sm font-medium text-gray-700">Scan from mail</label>
            <input type="text" id="scan_from" value={settings.emailScanner?.scanFrom || ''} onChange={e => setSettings(prev => ({ ...prev, emailScanner: { ...prev.emailScanner || {}, scanFrom: e.target.value || '' } }))} className="input" placeholder="admin@example.com" />
          </div>
          <div>
            <label htmlFor="helo_domain" className="block text-sm font-medium text-gray-700">HELO Domain</label>
            <input type="text" id="helo_domain" value={settings.emailScanner?.heloDomain || ''} onChange={e => setSettings(prev => ({ ...prev, emailScanner: { ...prev.emailScanner || {}, heloDomain: e.target.value || '' } }))} className="input" placeholder="example.com" />
            <div className="text-xs text-gray-500 mt-1">The domain used in the HELO command for SMTP connections.</div>
          </div>
          <div>
            <label htmlFor="smtp_command" className="block text-sm font-medium text-gray-700">SMTP Command</label>
            <select id="smtp_command" value={settings.emailScanner?.smtpCommand || 'EHLO'} onChange={e => setSettings(prev => ({ ...prev, emailScanner: { ...prev.emailScanner || {}, smtpCommand: e.target.value || 'EHLO' } }))} className="input">
              <option value="EHLO">EHLO</option>
              <option value="HELO">HELO</option>
            </select>
          </div>
          <div>
            <label htmlFor="smtp_port" className="block text-sm font-medium text-gray-700">SMTP Scan Port</label>
            <input type="number" id="smtp_port" value={settings.emailScanner?.smtpPort || 25} onChange={e => setSettings(prev => ({ ...prev, emailScanner: { ...prev.emailScanner || {}, smtpPort: parseInt(e.target.value) || 25 } }))} className="input" min="1" max="65535" />
          </div>
          <div>
            <label htmlFor="timeout_sec" className="block text-sm font-medium text-gray-700">scan timeout per email (in sec)</label>
            <input type="number" id="timeout_sec" value={settings.emailScanner?.timeoutSec || 15} onChange={e => setSettings(prev => ({ ...prev, emailScanner: { ...prev.emailScanner || {}, timeoutSec: parseInt(e.target.value) || 15 } }))} className="input" min="1" max="120" />
          </div>
          <div>
            <label htmlFor="batch_size" className="block text-sm font-medium text-gray-700">total email to batch process</label>
            <input type="number" id="batch_size" value={settings.emailScanner?.batchSize || 10} onChange={e => setSettings(prev => ({ ...prev, emailScanner: { ...prev.emailScanner || {}, batchSize: parseInt(e.target.value) || 10 } }))} className="input" min="1" max="1000" />
            <div className="text-xs text-gray-500 mt-1">each time fetch how many emails to verify. Increase for speed but may risk of wrong result.</div>
          </div>
        </div>
      </section>



      {/* 3. SMTP & IMAP Credentials */}
      <section className="card bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 mb-8">
        <div className="card-header flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center space-x-2">
            <Server className="h-6 w-6 text-blue-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">SMTP & IMAP Credentials</h2>
          </div>
        </div>

        {/* SMTP & IMAP Information Notification */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-4 mx-6 mt-6 rounded-r-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Why SMTP & IMAP Configuration?
              </h3>
              <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                <p className="mb-2">
                  <strong>SMTP verification significantly increases email accuracy</strong> by directly connecting to mail servers and checking if email addresses actually exist.
                </p>
                <p className="mb-2">
                  <strong>IMAP verification</strong> checks the inbox for our <strong>Inbox Verification Templates</strong> to confirm delivery and detect bounces, ensuring the most reliable results.
                </p>
                <p className="mb-2">
                  Together, these methods can detect invalid emails that might pass basic validation but don't actually exist or can't receive mail.
                </p>
                <p className="text-blue-600 dark:text-blue-400 font-medium">
                  💡 <strong>Tip:</strong> You can add or change Inbox Verification Templates from the bottom of this page in the "Inbox Verification Templates" section.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-6">
          {/* IMAP Verification Toggle */}
          <div className="col-span-2 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700">
            <div className="pr-4">
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Enable IMAP Verification</span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                When enabled, the system sends test emails via SMTP and checks your IMAP inbox to confirm delivery and detect bounces.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!!settings.deep_verification}
              onClick={() => setSettings(prev => ({ ...prev, deep_verification: !prev.deep_verification }))}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${settings.deep_verification ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${settings.deep_verification ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Test Email Input */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Test Email Address</label>
            <input
              type="email"
              className="input"
              value={testEmail || ''}
              onChange={e => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              required
            />
            <div className="text-xs text-gray-500 mt-1">This email will be used for SMTP and IMAP test connections.</div>
          </div>
          {/* SMTP Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700">SMTP Host</label>
            <input type="text" className="input" value={settings.smtp?.host || ''} onChange={e => updateSetting('smtp', 'host', e.target.value)} placeholder="smtp.example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">SMTP Port</label>
            <input type="number" className="input" value={settings.smtp?.port || 587} onChange={e => updateSetting('smtp', 'port', parseInt(e.target.value))} min="1" max="65535" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">SMTP Username</label>
            <input type="text" className="input" value={settings.smtp?.username || ''} onChange={e => updateSetting('smtp', 'username', e.target.value)} placeholder="user@example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">SMTP Password</label>
            <input type="password" className="input" onChange={e => updateSetting('smtp', 'password', e.target.value)} placeholder="••••••••" autoComplete="new-password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">From Name</label>
            <input type="text" className="input" value={settings.emailScanner?.fromName || ''} onChange={e => setSettings(prev => ({ ...prev, emailScanner: { ...prev.emailScanner || {}, fromName: e.target.value } }))} placeholder="System Administrator" />
            <div className="text-xs text-gray-500 mt-1">Display name used when sending test emails (e.g., "System Administrator" instead of just "admin@example.com")</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">SMTP Secure (SSL/TLS)</label>
            <select className="input" value={settings.smtp?.secure ? 'true' : 'false'} onChange={e => updateSetting('smtp', 'secure', e.target.value === 'true')}>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div className="col-span-2 flex items-center space-x-4 mt-2">
            <button className="btn btn-secondary" onClick={handleTestSmtp} disabled={isTesting} type="button">
              {isTesting ? 'Testing SMTP...' : 'Test SMTP'}
            </button>
            {testResult && (
              <span className={testResult.success ? 'text-green-600' : 'text-red-600'}>
                {testResult.success ? 'SMTP connection successful!' : (testResult.error || 'SMTP test failed')}
              </span>
            )}
          </div>

          {/* IMAP Section Information */}
          <div className="col-span-2 bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 p-4 rounded-r-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                  IMAP Verification Process
                </h3>
                <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                  <p className="mb-2">
                    <strong>IMAP verification</strong> sends our <strong>Inbox Verification Templates</strong> to test emails and then checks your inbox for:
                  </p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li><strong>Successful delivery</strong> - confirms the email address is valid and can receive mail</li>
                    <li><strong>Bounce detection</strong> - identifies invalid emails that get rejected by mail servers</li>
                    <li><strong>Real-time verification</strong> - provides the most accurate results possible</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* IMAP Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700">IMAP Host</label>
            <input type="text" className="input" value={settings.imap?.host || ''} onChange={e => updateSetting('imap', 'host', e.target.value)} placeholder="imap.example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">IMAP Port</label>
            <input type="number" className="input" value={settings.imap?.port || 993} onChange={e => updateSetting('imap', 'port', parseInt(e.target.value))} min="1" max="65535" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">IMAP Username</label>
            <input type="text" className="input" value={settings.imap?.username || ''} onChange={e => updateSetting('imap', 'username', e.target.value)} placeholder="user@example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">IMAP Password</label>
            <input type="password" className="input" onChange={e => updateSetting('imap', 'password', e.target.value)} placeholder="••••••••" autoComplete="new-password" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">IMAP TLS/SSL</label>
            <select className="input" value={settings.imap?.tls ? 'true' : 'false'} onChange={e => updateSetting('imap', 'tls', e.target.value === 'true')}>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">IMAP Wait After Send (seconds)</label>
            <input
              type="number"
              className="input"
              value={Math.round((settings.imap?.waitAfterSend || 5000) / 1000)}
              onChange={e => updateSetting('imap', 'waitAfterSend', parseInt(e.target.value || 5) * 1000)}
              min="1"
              max="60"
            />
            <div className="text-xs text-gray-500 mt-1">Time to wait after sending test email before starting IMAP search (1-60 seconds, default: 5)</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">IMAP Search Timeout (seconds)</label>
            <input
              type="number"
              className="input"
              value={Math.round((settings.imap?.searchTimeout || 15000) / 1000)}
              onChange={e => updateSetting('imap', 'searchTimeout', parseInt(e.target.value || 15) * 1000)}
              min="5"
              max="300"
            />
            <div className="text-xs text-gray-500 mt-1">Maximum time to spend searching IMAP inbox before timing out (5-300 seconds, default: 15)</div>
          </div>
          <div className="col-span-2 flex items-center space-x-4 mt-2">
            <button className="btn btn-secondary" onClick={handleTestImap} type="button">
              Test IMAP
            </button>
            {imapTestResult && (
              <span className={imapTestResult.success ? 'text-green-600' : 'text-red-600'}>
                {imapTestResult.success
                  ? 'IMAP connection successful!'
                  : (imapTestResult.message || imapTestResult.error || 'IMAP test failed')}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* 4. Upload Settings */}
      <section className="card bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 mb-8">
        <div className="card-header flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center space-x-2">
            <Upload className="h-6 w-6 text-blue-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Upload & Export Settings</h2>
          </div>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Max Concurrent Uploads</label>
            <input
              type="number"
              min={2}
              max={10}
              value={settings.uploads?.max_concurrent_uploads || 2}
              onChange={e => {
                const val = parseInt(e.target.value);
                if (!isNaN(val) && val >= 2 && val <= 10) {
                  setSettings(s => ({ ...s, uploads: { ...s.uploads, max_concurrent_uploads: val } }));
                  setError(null);
                } else {
                  setError('Max concurrent uploads must be between 2 and 10');
                }
              }}
              className="input"
            />
            {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
            <div className="text-xs text-gray-500 mt-1">How many lists can be uploaded and processed in parallel. Increase for more speed, decrease to limit server load.</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Max Concurrent Exports</label>
            <input
              type="number"
              min={1}
              max={5}
              value={settings.uploads?.max_concurrent_exports || 2}
              onChange={e => {
                const val = parseInt(e.target.value);
                if (!isNaN(val) && val >= 1 && val <= 5) {
                  setSettings(s => ({ ...s, uploads: { ...s.uploads, max_concurrent_exports: val } }));
                  setError(null);
                } else {
                  setError('Max concurrent exports must be between 1 and 5');
                }
              }}
              className="input"
            />
            {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
            <div className="text-xs text-gray-500 mt-1">How many export jobs can run simultaneously. Higher values improve export speed for multiple lists.</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Max Upload File Size (MB)</label>
            <input
              type="number"
              className="input"
              min="1"
              max="100"
              value={settings.uploads?.max_upload_size_mb || 10}
              onChange={e => setSettings(prev => ({
                ...prev,
                uploads: {
                  ...prev.uploads || {},
                  max_upload_size_mb: Math.max(1, Math.min(100, parseInt(e.target.value) || 10))
                }
              }))}
            />
            <div className="text-xs text-gray-500 mt-1">Maximum CSV upload size allowed for bulk verification (1–100 MB).</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Export Batch Size</label>
            <input
              type="number"
              className="input"
              min="100"
              max="5000"
              value={settings.uploads?.export_batch_size || 1000}
              onChange={e => setSettings(prev => ({
                ...prev,
                uploads: {
                  ...prev.uploads || {},
                  export_batch_size: Math.max(100, Math.min(5000, parseInt(e.target.value) || 1000))
                }
              }))}
            />
            <div className="text-xs text-gray-500 mt-1">Number of emails processed per batch during export. Higher values improve performance but use more memory.</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Max Email List Size</label>
            <input
              type="number"
              className="input"
              min="1000"
              max="1000000"
              value={settings.uploads?.max_email_list_size || 100000}
              onChange={e => setSettings(prev => ({
                ...prev,
                uploads: {
                  ...prev.uploads || {},
                  max_email_list_size: Math.max(1000, Math.min(1000000, parseInt(e.target.value) || 100000))
                }
              }))}
            />
            <div className="text-xs text-gray-500 mt-1">Maximum number of emails allowed in a single upload (1K–1M emails).</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Import Concurrency</label>
            <input
              type="number"
              className="input"
              min={1}
              max={6}
              value={settings.uploads?.import_concurrency || 4}
              onChange={e => {
                const val = parseInt(e.target.value);
                if (!isNaN(val) && val >= 1 && val <= 6) {
                  setSettings(prev => ({
                    ...prev,
                    uploads: {
                      ...prev.uploads || {},
                      import_concurrency: val
                    }
                  }));
                  setError(null);
                } else {
                  setError('Import concurrency must be between 1 and 6');
                }
              }}
            />
            {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
            <div className="text-xs text-gray-500 mt-1">Number of parallel workers for importing emails (1–6). Higher values use more CPU/DB connections but speed up large imports.</div>
          </div>
        </div>
      </section>

      {/* 5. Worker Concurrency & Background Processing Settings */}
      <section className="card bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 mb-8">
        <div className="card-header flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-6 w-6 text-blue-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Background Worker Settings</h2>
          </div>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Worker Concurrency (Jobs)</label>
            <input
              type="number"
              className="input"
              min="1"
              max="20"
              value={settings.uploads?.worker_concurrency || 2}
              onChange={e => setSettings(s => ({ ...s, uploads: { ...s.uploads, worker_concurrency: parseInt(e.target.value) || 2 } }))}
              disabled={isLoading || isSaving}
            />
            <div className="text-xs text-gray-500 mt-1">Number of jobs the background worker can process in parallel.</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">SMTP Concurrency (Per Job)</label>
            <input
              type="number"
              className="input"
              min="1"
              max="100"
              value={settings.uploads?.smtp_concurrency || 5}
              onChange={e => setSettings(s => ({ ...s, uploads: { ...s.uploads, smtp_concurrency: parseInt(e.target.value) || 5 } }))}
              disabled={isLoading || isSaving}
            />
            <div className="text-xs text-gray-500 mt-1">Number of emails processed in parallel per job.</div>
          </div>
        </div>
      </section>


      {/* Advanced Settings Accordion */}
      <div className="mb-8">
        <button
          type="button"
          id="advanced-settings-toggle"
          onClick={() => setShowAdvanced(prev => !prev)}
          className="w-full flex items-center justify-between px-6 py-4 bg-gray-900 dark:bg-gray-800 text-white rounded-2xl shadow-lg border border-gray-700 hover:bg-gray-800 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <SettingsIcon className="h-5 w-5 text-orange-400" />
            <span className="text-base font-bold tracking-wide">Advanced Settings</span>
            {!showAdvanced && (
              <span className="ml-3 px-2 py-0.5 text-xs font-semibold bg-orange-500 text-white rounded-full">Hidden</span>
            )}
          </div>
          <svg
            className={`h-5 w-5 text-gray-300 transition-transform duration-300 ${showAdvanced ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showAdvanced && (
          <div className="mt-4 space-y-8">

            {/* Warning Banner */}
            <div className="flex items-start space-x-3 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl px-5 py-4">
              <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-red-700 dark:text-red-400">&#9888;&#65039; Adjust only if you know what you are doing.</p>
                <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                  These are low-level operational settings that affect verification speed, IP reputation, satellite node behaviour,
                  and queue retry timing. Incorrect values can cause false results, IP bans, or stalled jobs.
                  Leave at defaults unless you have a specific reason to change them.
                </p>
              </div>
            </div>

            {/* Satellite Network Settings */}
            <section className="card bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800">
              <div className="card-header flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div className="flex items-center space-x-2">
                  <Globe className="h-6 w-6 text-blue-400" />
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Satellite Network Settings</h2>
                </div>
              </div>
              <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-6 px-6 py-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Enable Node Rotation</label>
                  <select className="input mt-1" value={settings.satelliteNetwork?.rotationEnabled ? 'true' : 'false'} onChange={e => setSettings(s => ({ ...s, satelliteNetwork: { ...s.satelliteNetwork, rotationEnabled: e.target.value === 'true' } }))}>
                    <option value="false">Disabled (All nodes process immediately)</option>
                    <option value="true">Enabled (Nodes rotate chunks sequentially)</option>
                  </select>
                  <div className="text-xs text-gray-500 mt-1">If enabled, verification chunks are assigned strictly round-robin based on processed volume.</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Emails Before Rotation</label>
                  <input type="number" className="input mt-1" min="50" max="100000" value={settings.satelliteNetwork?.rotationEmails || 500} onChange={e => setSettings(s => ({ ...s, satelliteNetwork: { ...s.satelliteNetwork, rotationEmails: parseInt(e.target.value) || 500 } }))} disabled={!settings.satelliteNetwork?.rotationEnabled} />
                  <div className="text-xs text-gray-500 mt-1">Number of emails a single node will process before passing the torch to the next online node.</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Master Node Verification</label>
                  <select className="input mt-1" value={settings.satelliteNetwork?.masterEnabled !== false ? 'true' : 'false'} onChange={e => setSettings(s => ({ ...s, satelliteNetwork: { ...s.satelliteNetwork, masterEnabled: e.target.value === 'true' } }))}>
                    <option value="true">Enabled (Master node verifies along with satellites)</option>
                    <option value="false">Disabled (Only satellite nodes verify emails)</option>
                  </select>
                  <div className="text-xs text-gray-500 mt-1">Disable this to 100% protect the Master Node&apos;s IP reputation by pushing all verification work out to Satellites.</div>
                </div>
              </div>
            </section>

            {/* Rate Limiting Settings */}
            <section className="card bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800">
              <div className="card-header flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="h-6 w-6 text-orange-400" />
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Rate Limiting Settings</h2>
                </div>
              </div>
              <div className="card-body space-y-6 px-6 py-6">
                <div>
                  <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-4">Provider Rate Limiting</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div><label className="block text-sm font-medium text-gray-700">Normal Cooldown (ms)</label><input type="number" className="input" min="100" max="10000" value={settings.rateLimiting?.normalCooldown || 1000} onChange={e => setSettings(s => ({ ...s, rateLimiting: { ...s.rateLimiting, normalCooldown: parseInt(e.target.value) || 1000 } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">Cooldown between normal requests (1000ms = 1 second)</div></div>
                    <div><label className="block text-sm font-medium text-gray-700">Yahoo/AOL Cooldown (ms)</label><input type="number" className="input" min="60000" max="14400000" value={settings.rateLimiting?.yahooAolCooldown || 7200000} onChange={e => setSettings(s => ({ ...s, rateLimiting: { ...s.rateLimiting, yahooAolCooldown: parseInt(e.target.value) || 7200000 } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">Rate limit cooldown for Yahoo/AOL (7200000ms = 2 hours)</div></div>
                    <div><label className="block text-sm font-medium text-gray-700">Microsoft Cooldown (ms)</label><input type="number" className="input" min="60000" max="1800000" value={settings.rateLimiting?.microsoftCooldown || 300000} onChange={e => setSettings(s => ({ ...s, rateLimiting: { ...s.rateLimiting, microsoftCooldown: parseInt(e.target.value) || 300000 } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">Rate limit cooldown for Microsoft (300000ms = 5 minutes)</div></div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-100 dark:border-gray-800">
                  <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center"><Shield className="h-5 w-5 mr-2 text-blue-400" />Daily Provider Limits (Per Node)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><label className="block text-sm font-medium text-gray-700">EU Providers Daily Limit</label><input type="number" className="input" min="0" value={settings.rateLimiting?.btDailyLimit || 1500} onChange={e => setSettings(s => ({ ...s, rateLimiting: { ...s.rateLimiting, btDailyLimit: parseInt(e.target.value) || 0 } }))} /><div className="text-xs text-gray-500 mt-1">Maximum BT/Openworld emails each node will verify per day.</div></div>
                    <div><label className="block text-sm font-medium text-gray-700">SEA Providers Daily Limit</label><input type="number" className="input" min="0" value={settings.rateLimiting?.talktalkDailyLimit || 1000} onChange={e => setSettings(s => ({ ...s, rateLimiting: { ...s.rateLimiting, talktalkDailyLimit: parseInt(e.target.value) || 0 } }))} /><div className="text-xs text-gray-500 mt-1">Maximum TalkTalk/Tiscali emails each node will verify per day.</div></div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-100 dark:border-gray-800">
                  <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center"><RefreshCw className="h-5 w-5 mr-2 text-blue-400" />Global Batch Throttling</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><label className="block text-sm font-medium text-gray-700">Global Batch Limit Size</label><input type="number" className="input" min="1" value={settings.rateLimiting?.batchLimitSize || 50} onChange={e => setSettings(s => ({ ...s, rateLimiting: { ...s.rateLimiting, batchLimitSize: parseInt(e.target.value) || 1 } }))} /><div className="text-xs text-gray-500 mt-1">Number of emails to process before pausing (affects ALL providers).</div></div>
                    <div><label className="block text-sm font-medium text-gray-700">Global Batch Delay (Seconds)</label><input type="number" className="input" min="0" value={settings.rateLimiting?.batchDelaySec || 15} onChange={e => setSettings(s => ({ ...s, rateLimiting: { ...s.rateLimiting, batchDelaySec: parseInt(e.target.value) || 0 } }))} /><div className="text-xs text-gray-500 mt-1">Seconds to wait between batches to protect IP reputation.</div></div>
                  </div>
                </div>
                <div className="pt-6 border-t border-gray-100 dark:border-gray-800">
                  <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-4">Retry Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><label className="block text-sm font-medium text-gray-700">Retry Attempts</label><input type="number" className="input" min="1" max="10" value={settings.rateLimiting?.retryAttempts || 3} onChange={e => setSettings(s => ({ ...s, rateLimiting: { ...s.rateLimiting, retryAttempts: parseInt(e.target.value) || 3 } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">Number of retry attempts for failed verifications</div></div>
                    <div><label className="block text-sm font-medium text-gray-700">Progress Update Frequency</label><input type="number" className="input" min="1" max="100" value={settings.rateLimiting?.progressUpdateFreq || 10} onChange={e => setSettings(s => ({ ...s, rateLimiting: { ...s.rateLimiting, progressUpdateFreq: parseInt(e.target.value) || 10 } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">Update dashboard progress every N emails</div></div>
                  </div>
                </div>
                <div>
                  <h3 className="text-md font-semibold text-gray-800 mb-4">Retry Delays</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div><label className="block text-sm font-medium text-gray-700">First Retry Delay (ms)</label><input type="number" className="input" min="1000" max="30000" value={settings.rateLimiting?.retryDelay1 || 3000} onChange={e => setSettings(s => ({ ...s, rateLimiting: { ...s.rateLimiting, retryDelay1: parseInt(e.target.value) || 3000 } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">Delay before first retry (3000ms = 3 seconds)</div></div>
                    <div><label className="block text-sm font-medium text-gray-700">Second Retry Delay (ms)</label><input type="number" className="input" min="1000" max="60000" value={settings.rateLimiting?.retryDelay2 || 6000} onChange={e => setSettings(s => ({ ...s, rateLimiting: { ...s.rateLimiting, retryDelay2: parseInt(e.target.value) || 6000 } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">Delay before second retry (6000ms = 6 seconds)</div></div>
                    <div><label className="block text-sm font-medium text-gray-700">Third Retry Delay (ms)</label><input type="number" className="input" min="1000" max="60000" value={settings.rateLimiting?.retryDelay3 || 9000} onChange={e => setSettings(s => ({ ...s, rateLimiting: { ...s.rateLimiting, retryDelay3: parseInt(e.target.value) || 9000 } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">Delay before third retry (9000ms = 9 seconds)</div></div>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3"><AlertTriangle className="h-5 w-5 text-blue-500 mt-0.5" /><div><h4 className="text-sm font-medium text-blue-800">Rate Limiting Information</h4><p className="text-sm text-blue-700 mt-1">These settings control how the system handles provider rate limits. Lower values increase verification speed but may trigger rate limits more often. Higher values are more conservative but slower.</p></div></div>
                </div>
              </div>
            </section>

            {/* Queue Retry Settings */}
            <section className="card bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800">
              <div className="card-header flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div className="flex items-center space-x-2">
                  <RefreshCw className="h-6 w-6 text-purple-400" />
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Queue Retry Settings</h2>
                </div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-400 p-4 mx-6 mt-6 rounded-r-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0"><svg className="h-5 w-5 text-purple-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg></div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-purple-800 dark:text-purple-200">Multi-Tier Queue System for Rate-Limited Emails</h3>
                    <div className="mt-2 text-sm text-purple-700 dark:text-purple-300">
                      <p className="mb-2">When emails are rate-limited by providers (especially Yahoo/AOL), they are queued for retry with increasing delays across multiple tiers:</p>
                      <ul className="list-disc list-inside ml-2 space-y-1">
                        <li><strong>Tier 1:</strong> Quick retry after a few minutes (for temporary rate limits)</li>
                        <li><strong>Tier 2:</strong> Extended cooldown of 1+ hours (for persistent rate limits)</li>
                        <li><strong>Tier 3:</strong> Day-long cooldown (for severe rate limiting)</li>
                        <li><strong>Tier 4:</strong> Multi-day cooldown (final attempt before marking as Unknown)</li>
                      </ul>
                      <p className="mt-2 text-purple-600 dark:text-purple-400 font-medium">&#128161; <strong>Tip:</strong> Jobs will remain in &quot;processing&quot; status until all queued emails complete their retries.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-body space-y-6 px-6 py-6">
                <div>
                  <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-4">Retry Configuration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><label className="block text-sm font-medium text-gray-700">Max Retry Attempts</label><input type="number" className="input" min="1" max="10" value={settings.queueRetry?.maxRetryAttempts || 5} onChange={e => setSettings(s => ({ ...s, queueRetry: { ...s.queueRetry, maxRetryAttempts: Math.max(1, Math.min(10, parseInt(e.target.value) || 5)) } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">Maximum number of retry attempts before marking email as Unknown (1-10, default: 5)</div></div>
                  </div>
                </div>
                <div>
                  <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-4">Tier Cooldown Delays</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div><label className="block text-sm font-medium text-gray-700">Tier 1 Delay (minutes)</label><input type="number" className="input" min="1" max="60" value={settings.queueRetry?.tier1DelayMinutes || 2} onChange={e => setSettings(s => ({ ...s, queueRetry: { ...s.queueRetry, tier1DelayMinutes: Math.max(1, Math.min(60, parseInt(e.target.value) || 2)) } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">First retry delay in minutes (default: 2 minutes)</div></div>
                    <div><label className="block text-sm font-medium text-gray-700">Tier 2 Delay (hours)</label><input type="number" className="input" min="1" max="24" value={settings.queueRetry?.tier2DelayHours || 1} onChange={e => setSettings(s => ({ ...s, queueRetry: { ...s.queueRetry, tier2DelayHours: Math.max(1, Math.min(24, parseInt(e.target.value) || 1)) } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">Second retry delay in hours (default: 1 hour)</div></div>
                    <div><label className="block text-sm font-medium text-gray-700">Tier 3 Delay (hours)</label><input type="number" className="input" min="1" max="72" value={settings.queueRetry?.tier3DelayHours || 24} onChange={e => setSettings(s => ({ ...s, queueRetry: { ...s.queueRetry, tier3DelayHours: Math.max(1, Math.min(72, parseInt(e.target.value) || 24)) } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">Third retry delay in hours (default: 24 hours)</div></div>
                    <div><label className="block text-sm font-medium text-gray-700">Tier 4 Delay (hours)</label><input type="number" className="input" min="1" max="168" value={settings.queueRetry?.tier4DelayHours || 48} onChange={e => setSettings(s => ({ ...s, queueRetry: { ...s.queueRetry, tier4DelayHours: Math.max(1, Math.min(168, parseInt(e.target.value) || 48)) } }))} disabled={isLoading || isSaving} /><div className="text-xs text-gray-500 mt-1">Fourth (final) retry delay in hours (default: 48 hours)</div></div>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3"><AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" /><div><h4 className="text-sm font-medium text-amber-800">Important Notes</h4><p className="text-sm text-amber-700 mt-1">&#8226; Longer delays give providers more time to reset rate limits, improving success rates<br />&#8226; Jobs remain in &quot;processing&quot; status until all queued emails complete their retries<br />&#8226; After max attempts are exhausted, emails are marked as &quot;Unknown&quot;<br />&#8226; Changes take effect immediately for new verifications</p></div></div>
                </div>
              </div>
            </section>

            {/* Provider Concurrency Settings */}
            <section className="card bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800">
              <div className="card-header flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div className="flex items-center space-x-2">
                  <SettingsIcon className="h-6 w-6 text-green-400" />
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Provider Concurrency Settings</h2>
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 p-4 mx-6 mt-6 rounded-r-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0"><svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg></div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800 dark:text-green-200">Connection Limits Per Provider</h3>
                    <div className="mt-2 text-sm text-green-700 dark:text-green-300"><p>Control the maximum number of concurrent SMTP connections allowed for specific mail providers. Lowering these values can help avoid &quot;Too many connections&quot; errors and IP blacklisting.</p></div>
                  </div>
                </div>
              </div>
              <div className="card-body grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 px-6 py-6">
                {Object.keys(settings.providerConcurrency || {}).map(provider => (
                  <div key={provider}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 capitalize mb-1">{provider === 'default' ? 'Other Providers' : provider}</label>
                    <input type="number" className="input" min="1" max="100" value={settings.providerConcurrency[provider] || 5} onChange={e => setSettings(s => ({ ...s, providerConcurrency: { ...s.providerConcurrency, [provider]: parseInt(e.target.value) || 1 } }))} disabled={isLoading || isSaving} />
                  </div>
                ))}
              </div>
            </section>

          </div>
        )}
      </div>{/* end Advanced Settings Accordion */}
      {/* 8. Inbox Verification Templates */}
      <section className="card bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-800 mb-8">
        <div className="card-header flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center space-x-2">
            <Mail className="h-6 w-6 text-blue-400" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Inbox Verification Templates</h2>
          </div>
        </div>
        <div className="card-body px-6 py-6">
          {/* Explanation Section */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">Why Inbox Verification Templates?</h3>
            <p className="text-sm text-blue-800 mb-3">
              Inbox verification templates are used in the <strong>IMAP Inbox Checker</strong> verification method to test email deliverability.
              Here's how it works:
            </p>
            <ul className="text-sm text-blue-800 space-y-1 ml-4">
              <li>• <strong>Send Test Email:</strong> A verification template is sent to the target email address via SMTP</li>
              <li>• <strong>Check Inbox:</strong> The system connects to your IMAP inbox to check if the email was delivered</li>
              <li>• <strong>Detect Bounces:</strong> If the email bounces back, it indicates the address is invalid</li>
              <li>• <strong>Verify Delivery:</strong> If the email appears in your inbox, the address is likely valid</li>
            </ul>
            <p className="text-sm text-blue-800 mt-3">
              <strong>Note:</strong> IMAP verification is used when SMTP verification alone cannot provide a definitive result. This includes:
            </p>
            <ul className="text-sm text-blue-800 space-y-1 ml-4 mt-2">
              <li>• <strong>Catch-all domains</strong> that accept all emails regardless of validity</li>
              <li>• <strong>Corporate mail servers</strong> with strict security policies</li>
              <li>• <strong>Any provider</strong> where SMTP returns "Unknown" or inconclusive responses</li>
              <li>• <strong>Custom mail servers</strong> with non-standard SMTP behavior</li>
            </ul>
            <p className="text-sm text-blue-800 mt-2">
              IMAP verification provides the most accurate results by actually sending test emails and checking for delivery or bounces.
            </p>
          </div>

          {/* Add New Verification Template */}
          <div className="mb-6 p-4 border border-gray-200 rounded-lg">
            <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">Add New Verification Template</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                <input
                  type="text"
                  className="input"
                  value={newDummyEmail.subject}
                  onChange={e => setNewDummyEmail(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="e.g., Sorry, sent by mistake for system error"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message Content</label>
                <textarea
                  className="input min-h-[80px]"
                  value={newDummyEmail.message}
                  onChange={e => setNewDummyEmail(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Enter the email message content (HTML supported)"
                />
              </div>
            </div>
            <div className="mt-3">
              <button
                className="btn btn-primary"
                onClick={handleAddDummyEmail}
                disabled={!newDummyEmail.subject || !newDummyEmail.message}
              >
                Add Verification Template
              </button>
            </div>
          </div>

          {/* Existing Verification Templates */}
          <div>
            <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">Existing Verification Templates</h3>
            {dummyEmails.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Mail className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No verification templates configured yet.</p>
                <p className="text-sm">Add your first verification template above to get started.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {dummyEmails.map((email) => (
                  <div key={email.id} className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-white dark:bg-gray-900">
                    {editingDummyEmail?.id === email.id ? (
                      // Edit Mode
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                          <input
                            type="text"
                            className="input"
                            value={editingDummyEmail.subject}
                            onChange={e => setEditingDummyEmail(prev => ({ ...prev, subject: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                          <textarea
                            className="input min-h-[100px]"
                            value={editingDummyEmail.message}
                            onChange={e => setEditingDummyEmail(prev => ({ ...prev, message: e.target.value }))}
                          />
                        </div>
                        <div className="flex space-x-2">
                          <button
                            className="btn btn-primary"
                            onClick={() => {
                              handleUpdateDummyEmail(email.id, editingDummyEmail.subject, editingDummyEmail.message);
                              setEditingDummyEmail(null);
                            }}
                            disabled={!editingDummyEmail.subject || !editingDummyEmail.message}
                          >
                            Save Changes
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => setEditingDummyEmail(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div>
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">Subject: {email.sub}</h4>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                              <div className="mb-2">
                                <strong>Message Preview:</strong>
                              </div>
                              <div
                                className="bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 p-3 rounded border text-sm max-h-32 overflow-y-auto"
                                dangerouslySetInnerHTML={{ __html: email.msg }}
                              />
                            </div>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingDummyEmail({ id: email.id, subject: email.sub, message: email.msg })}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => {
                                if (window.confirm('Are you sure you want to delete this verification template?')) {
                                  handleDeleteDummyEmail(email.id);
                                }
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Settings Guide Modal */}
      {
        showSettingsGuide && (
          <SettingsGuide onClose={() => setShowSettingsGuide(false)} />
        )
      }
    </div >
  )
} 